import React, { Component } from 'react'
import { graphql, compose, withApollo } from 'react-apollo'
import { Constants, NoteSection, ProblemSection } from '@sukiai/components'
import SectionEditor from './SectionEditor'
import { logger, halfStory as hs } from '../../lib/logging'
import { sendSpeechConfig, getStreamWorker } from '../../ws/stream'
import {
  scrollToSection,
  scrollToSidebarSection,
  HALF_STORY,
  promptICD10Lookup,
  setSectionDictation,
  formatCode,
  updateSectionInCache,
  LO_STO,
  SESH_STO,
  play,
  stopSectionDictation,
  setToast,
  getCompFromCache,
  getPbSectionCtx,
  deleteProblemSection,
  autoPopulateICD10AfterAdd,
  updatePbcSectionInCache
} from '../../lib'
import {
  GET_CURRENT_NOTETYPE_ID,
  GET_KEYBOARD_MODE,
  SET_MODAL,
  SET_AGENT_STATE,
  SET_CURSOR_POSITION,
  SET_SECTION,
  SET_KEYBOARD_MODE,
  GET_DICTATION,
  SET_CURSOR_ACKNOWLEDGED,
  SET_AUDIO_PROCESSED
} from '../../apollo/actions'
import client from '../../apollo'
import omitDeep from 'omit-deep-lodash'
import debounce from 'lodash/debounce'
import get from 'lodash.get'
import isNil from 'lodash/isNil'
import findIndex from 'lodash/findIndex'
// import { INTERCOM_EVENTS } from '../../lib/constants'
import { DataCommand } from '../../ws/DataCommand'
import loSto from '../../config/loSto'
import seshSto from '../../config/seshSto'
import { handleContentFocus } from '../../lib/util/scroll'
import { isAmwellTheme } from '../../lib/util'

const { SECTION_STATE, AGENT, MODALS, SOUNDS, TOAST } = Constants

class Section extends Component {
  state = {
    content: this.props.initialContent,
    status: this.props.initialStatus,
    sectionNameValue: this.props.isNewSection ? '' : this.props.name
  }

  componentWillReceiveProps (nextProps) {
    const {
      id,
      compositionId,
      shouldUpdate,
      currentSectionId,
      currentSectionState,
      name,
      setSection
    } = this.props

    if (!nextProps.keyboardMode && this.props.keyboardMode) {
      this.updateSectionFlush()
    }

    if (!shouldUpdate && nextProps.shouldUpdate) {
      logger.info(`[Editor] Updating section ${id} from props and removing from sections to update in composition ${compositionId}`)
      console.info(`[Editor] Updating section ${id} due to STU`)

      if (currentSectionId === id && currentSectionState === SECTION_STATE.INSERTING) {
        this.animateInsertion(nextProps.shouldUpdate)
      } else {
        this.updateSectionState(nextProps.shouldUpdate)
        this.scrollIfNeeded()
      }
    }

    if (name !== nextProps.name) {
      this.setState({ sectionNameValue: nextProps.name })
    }

    if (currentSectionId !== nextProps.currentSectionId && this.props.keyboardMode && currentSectionState !== nextProps.currentSectionState &&
        currentSectionId !== null) {
      setSection(currentSectionId, SECTION_STATE.FOCUSED)
    }
  }

  componentWillUnmount () {
    this.updateSectionFlush()
  }

  onSectionNameChange = val => {
    this.setState({ sectionNameValue: val })
    handleContentFocus()
  }

  // [TODO] SECTION STATUS - USED HERE FOR V2 SECTIONS
  updateSectionState = updatedSection => {
    const { compositionId, removeSectionToUpdate } = this.props

    return new Promise(resolve => {
      this.setState({
        content: updatedSection.content,
        status: updatedSection.status,
        sectionNameValue: updatedSection.name,
        cursorPosition: updatedSection.cursorPosition
      }, () => {
        // set values to cache
        updateSectionInCache(compositionId, updatedSection)

        console.debug('[Editor] Removing section from STU after update')
        removeSectionToUpdate(updatedSection.id)
        resolve()
      })
    })
  }

  updateSectionFlush = () => {
    // flush update debounces
    this.updateSection.flush()
  }

  animateInsertion = updatedSection => {
    const el = document.getElementById(updatedSection.id)

    // Get initial height and hide overflow
    const initialHeight = el.scrollHeight
    el.style.height = `${initialHeight}px`
    el.style.overflowY = 'hidden'

    // Update content and status
    this.updateSectionState(updatedSection).then(() => {
      // Get final height and calculate delta
      const finalHeight = el.scrollHeight
      const delta = finalHeight - initialHeight
      const DURATION = 500
      let start = ''
      let end = ''

      const tick = ts => {
        if (!start) {
          // Set start and end timestamps
          start = ts
          end = ts + DURATION

          window.requestAnimationFrame(time => tick(time))
        } else if (ts < end) {
          // Update height smoothly over duration
          const percent = (ts - start) / DURATION

          if (percent > 0) {
            const height = (percent * delta) + initialHeight
            el.style.height = `${height}px`
          }

          window.requestAnimationFrame(time => tick(time))
        } else {
          // Reset styles on completion
          el.style.height = 'auto'
          el.style.overflowY = ''
          this.scrollIfNeeded()
        }
      }

      window.requestAnimationFrame(time => tick(time))
      this.setAgentAfterUpdate()
    })
  }

  setAgentAfterUpdate = () => {
    const {
      id,
      removeSectionToUpdate,
      currentSectionId,
      currentSectionState,
      setSection,
      dictation,
      setAudioStreamContext,
      setAgentState
    } = this.props

    console.debug('[Editor] Removing section from STU after update')
    removeSectionToUpdate(id)

    if (currentSectionId === id && currentSectionState === SECTION_STATE.INSERTING) {
      if (dictation) {
        setSection(id, SECTION_STATE.DICTATION)
        setAgentState(AGENT.STATE.LISTENING)

        setAudioStreamContext({
          agentAwake: false,
          transcription: true,
          isScriptChange: false,
          sectionId: id,
          patientId: ''
        }).then(() => sendSpeechConfig('Sending speech configuration after switching sections.'))
      } else {
        setSection(id, SECTION_STATE.FOCUSED)
        setAgentState(AGENT.STATE.READY)

        setAudioStreamContext({
          agentAwake: false,
          transcription: false,
          isScriptChange: false,
          sectionId: '',
          patientId: ''
        }).then(() => sendSpeechConfig('Sending speech configuration after insertion.'))
      }
    }
  }

  scrollIfNeeded = () => {
    const { id, currentSectionId } = this.props

    if (currentSectionId === id) {
      scrollToSection(id)
    }
  }

  updateSection = debounce((sectionData, sectionDataToUpdateCache = {}) => {
    const {
      client,
      compositionId,
      dictation,
      keyboardMode
    } = this.props

    if (keyboardMode && !dictation) {
      if (sectionData.cursorPosition > sectionData.plainText.length) {
        console.error('Cursor position is greater than length of text in section data!')
        logger.info(`Cursor position is greater than length of text in section data! Setting cursor position to plainText.length: ${sectionData.id}]`)
        sectionData.cursorPosition = sectionData.plainText.length
      }

      // update cache optimistically
      updateSectionInCache(compositionId, sectionData)
      !_.isEmpty(sectionDataToUpdateCache) && updatePbcSectionInCache(compositionId, sectionDataToUpdateCache)

      client.query({
        query: GET_CURRENT_NOTETYPE_ID
      }).then(({ data: { currentNotetypeId } }) => {
        client.mutate({
          mutation: SET_CURSOR_POSITION,
          variables: {
            sectionId: sectionData.id,
            cursorPositionIndex: sectionData.cursorPosition,
            location: 'CONTENT'
          }
        })

        const stream = getStreamWorker()
        stream && stream.postMessage({
          cmd: DataCommand.USER_REQUEST,
          userRequestType: 'UPDATE_SECTION',
          userRequestData: {
            compositionId,
            sectionData: sectionData,
            sectionId: sectionData?.id,
            notetypeId: currentNotetypeId
          },
          ts: new Date().toISOString()
        })
      })
    }
  }, 800)

  updateValueOnEdit = (updatedContent, cursorPosition, plainText) => {
    const {
      id,
      name,
      initialContent,
      section
    } = this.props

    const { status } = this.state

    const newStatus = status

    const de = section && omitDeep(section.diagnosisEntry, '__typename')

    if (initialContent !== updatedContent) {
      const updatedSection = {
        ...section,
        id,
        name,
        content: updatedContent,
        status: newStatus,
        pbcSectionFlag: section.pbcSectionFlag,
        diagnosisEntry: de,
        plainText
      }

      if (cursorPosition !== null) {
        updatedSection.cursorPosition = cursorPosition
      }

      this.setState({ content: updatedContent })

      // Interfering with updates: https://github.com/apollographql/apollo-client/issues/1564
      delete updatedSection.__typename

      const dataToUpdateSectionInCache = {
        id,
        name,
        content: updatedContent,
        status: newStatus,
        pbcSectionFlag: section.pbcSectionFlag,
        diagnosisEntry: de,
        plainText
      }

      return this.updateSection(updatedSection, dataToUpdateSectionInCache)
    }
  }

  nextTabSection = (sections, currentSectionId, isShiftTab) => {
    const index = findIndex(sections,
      (section) => section.id === currentSectionId
    )

    if (index === -1) {
      return null
    }

    if (isShiftTab) {
      return sections[index - 1] ?? null
    } else {
      return sections[index + 1] ?? null
    }
  }

  handleKeyPress = (isShiftTab) => {
    const {
      id,
      currentSectionId,
      compositionId,
      keyboardMode,
      setSection,
      setAudioStreamContext,
      agentState,
      client,
      sectionsData
    } = this.props

    let nextSection
    if (isNil(currentSectionId) && keyboardMode && id) {
      nextSection = this.nextTabSection(sectionsData, id, isShiftTab)
    } else {
      nextSection = this.nextTabSection(sectionsData, currentSectionId, isShiftTab)
    }

    if (isNil(nextSection)) {
      return
    }

    const nextSectionId = nextSection.id

    // If tab is pressed in keyboard mode
    if (nextSectionId && (currentSectionId === id || (isNil(currentSectionId) && currentSectionId !== id)) && keyboardMode) {
      client.mutate({
        mutation: SET_SECTION,
        variables: { id: nextSectionId, state: SECTION_STATE.TYPING }
      })
    }

    // If tab is pressed in dictation mode
    if (nextSectionId && currentSectionId === id && !keyboardMode) {
      // send event to streamworker
      logger.info(
          `[S2 ALERT - CLIENT WAS IN AUDIO MODE & WE RECEIVED A FOCUS ON SECTION ${nextSectionId}]`
      )
      // Set CLICK TO DICTATE to currentSectionId
      setSectionDictation(compositionId, nextSectionId)

      scrollToSection(nextSectionId, isShiftTab)

      play(SOUNDS.END_OF_UTTERANCE)

      hs.add(
        HALF_STORY.CHANGE_SECTION,
          `Sending CHANGE_SECTION. Section [${currentSectionId}] => [${nextSectionId}]`
      )
      logger.info(
        'SENDING CHANGE_SECTION FROM LINE 320 IN AUDIO MODE ONSECTIONCLICK FUNCTION IN SECTION.JS'
      )

      const stream = getStreamWorker()
      stream &&
          nextSectionId &&
          stream.postMessage({
            cmd: DataCommand.USER_REQUEST,
            userRequestType: 'CHANGE_SECTION',
            userRequestData: {
              // WE SHOULD NOT SEND CHANGE_SECTION REQUEST IF SECTION_ID IS NULL
              sectionId: nextSectionId
            },
            ts: new Date().toISOString()
          })
    }
  }

  // ISSUE 1: WE ARE SENDING CHANGE_SECTION BEFORE AUDIO_START (TO FIX)
  // ISSUE 2: CHANGE BADGE_STATE ONLY AFTER AUDIO_START IS SENT

  onSectionClick = (e) => {
    const { id, currentSectionId, compositionId, keyboardMode, dictation } = this.props

    if (!isAmwellTheme()) {
      scrollToSidebarSection(id)
    }

    // WHEN USERS FIRST GET INTO DICTATION MODE
    if (
      (currentSectionId !== id || !dictation) &&
      !keyboardMode
    ) {
      // send event to streamworker
      logger.info(`[S2 ALERT - CLIENT WAS IN AUDIO MODE & WE RECEIVED A FOCUS ON SECTION ${id}]`)
      // Set CLICK TO DICTATE to currentSectionId
      setSectionDictation(compositionId, id)

      play(SOUNDS.END_OF_UTTERANCE)

      hs.add(HALF_STORY.CHANGE_SECTION, `Sending CHANGE_SECTION. Section [${currentSectionId}] => [${id}]`)
      logger.info('SENDING CHANGE_SECTION FROM LINE 320 IN AUDIO MODE ONSECTIONCLICK FUNCTION IN SECTION.JS')
      const stream = getStreamWorker()
      stream && id && stream.postMessage({
        cmd: DataCommand.USER_REQUEST,
        userRequestType: 'CHANGE_SECTION',
        userRequestData: {
          // WE SHOULD NOT SEND CHANGE_SECTION REQUEST IF SECTION_ID IS NULL
          sectionId: id
        },
        ts: new Date().toISOString()
      })
    }
  }

  onDictationClick = () => {
    const {
      currentSectionState,
      currentSectionId,
      id
    } = this.props

    // SCENARIOS
    // 1. New Section -> Set Dict
    // 2. Same but not dict -> Set Dict
    // 3. Same and already dict => Stop Dict

    if (currentSectionState === SECTION_STATE.DICTATION && currentSectionId === id) {
      this.stopSectionDictation()
    } else {
      if (currentSectionId !== id) {
        client.query({
          query: GET_KEYBOARD_MODE
        }).then(({ data: { keyboardMode } }) => {
          if (!keyboardMode) {
            hs.add(HALF_STORY.CHANGE_SECTION, `Sending CHANGE_SECTION. Section [${currentSectionId}] => [${id}]`)
            logger.info('SENDING CHANGE_SECTION FROM LINE 355 IN AUDIO MODE ONDICTATIONCLICK FUNCTION IN SECTION.JS')
            // send event to streamworker
            const stream = getStreamWorker()
            stream && stream.postMessage({
              cmd: DataCommand.USER_REQUEST,
              userRequestType: 'CHANGE_SECTION',
              userRequestData: {
                // WE SHOULD NOT SEND CHANGE_SECTION REQUEST IF SECTION_ID IS NULL
                sectionId: id
              },
              ts: new Date().toISOString()
            })
          }
        })
      }

      this.setSectionDictation()
    }
  }

  stopSectionDictation = (options) => {
    const { setAudioStreamContext, stopDictation } = this.props
    setAudioStreamContext({
      agentAwake: false,
      transcription: false,
      isScriptChange: false,
      sectionId: '',
      patientId: ''
    }).then(() => {
      stopDictation(options)
    })
  }

  setSectionDictation = () => {
    const { id, setAudioStreamContext, setAgentState } = this.props
    setAudioStreamContext({
      agentAwake: false,
      transcription: true,
      isScriptChange: false,
      sectionId: id,
      patientId: ''
    }).then(() => {
      hs.add(HALF_STORY.VOICE, `Section Dictation Command Triggered on Section ID: ${id}`)
      sendSpeechConfig(`Section Dictation Command Triggered on Section ID: ${id}`)
      setSectionDictation(id)
      setAgentState(AGENT.STATE.LISTENING)
    })
  }

  promptCodePreview = async () => {
    const type = MODALS.ICD10_PREVIEW
    const diagnosis = get(this.props, 'section.diagnosisEntry.diagnosis')
    const heading = diagnosis.description
    const body = formatCode(diagnosis.icdCode)
    const { id } = this.props
    const { sectionNameValue: name, content } = this.state

    const items = [{
      __typename: 'ModalItem',
      id,
      name
    }]

    await this.stopSectionDictation({ disableExitSound: true })

    client.mutate({
      mutation: SET_MODAL,
      variables: {
        content: {
          __typename: 'ModalContent',
          type,
          heading,
          body,
          items,
          data: {
            __typename: 'ModalData',
            fromConfirm: false,
            sectionContent: content
          }
        }
      }
    })
  }

  renderEditor = () => {
    const {
      id,
      name,
      currentSectionId,
      currentSectionState,
      compositionId,
      agentState,
      scripts,
      contentToInsert
    } = this.props

    const {
      cursorPosition
    } = this.state

    return (
      <SectionEditor
        id={id}
        name={name}
        content={this.state.content}
        compositionId={compositionId}
        currentSectionId={currentSectionId}
        cursorPosition={cursorPosition}
        currentSectionState={currentSectionState}
        updateValueOnEdit={this.updateValueOnEdit}
        agentState={agentState}
        updateSectionFlush={this.updateSectionFlush}
        handleTabKeyPress={this.handleKeyPress}
        scripts={scripts}
        contentToInsert={contentToInsert}
      />
    )
  }

  onNameInputClick = (e) => {
    const { id, dictation, keyboardMode } = this.props
    const { selectionStart, selectionEnd } = e.target

    if (dictation && !keyboardMode) {
      const stream = getStreamWorker()
      stream && stream.postMessage({
        cmd: DataCommand.CURSOR_POSITION,
        cursorData: {
          cursorPositionIndex: selectionStart,
          cursorPositionEndIndex: selectionEnd,
          sectionId: id,
          location: 'TITLE'
        },
        ts: new Date().toISOString()
      })
    }
  }

  onNameKeyDown = async (e) => {
    const {
      id,
      keyboardMode,
      setSection,
      setAudioStreamContext,
      agentState
    } = this.props

    if (e.key === 'Enter') { e.preventDefault() }

    if (!keyboardMode) {
      await client.mutate({
        mutation: SET_AUDIO_PROCESSED,
        variables: {
          isProcessed: false
        }
      })

      client.mutate({
        mutation: SET_CURSOR_ACKNOWLEDGED,
        variables: {
          isAcknowleded: true
        }
      })

      // no need to clear section or pass current section id
      stopSectionDictation('hm', false, id)

      // Wait for stop dictation and then enter typing & send audio_end
      // Put user in keyboard mode
      if (agentState !== AGENT.STATE.MUTED) {
        await client.mutate({
          mutation: SET_AGENT_STATE,
          variables: {
            agentState: AGENT.STATE.MUTED
          }
        })
        // Turning off the badge above should trigger KEYBOARD_ON
        await client.mutate({
          mutation: SET_SECTION,
          variables: {
            id,
            state: SECTION_STATE.TYPING
          }
        })
      }
      client.mutate({
        mutation: SET_KEYBOARD_MODE
      })
      seshSto.session(SESH_STO.KEYBOARD_MODE, true)
      logger.info('[S2 ALERT - CLIENT ENTERING KEYBOARD MODE AND TURNING OFF THE BADGE - NAME VALUE on PBC SECTION TITLE CLICKED]')

      await setAudioStreamContext({
        agentAwake: false,
        transcription: false,
        isScriptChange: false,
        section: id
      })

      sendSpeechConfig('Sending new speech config on STOP_DICTATION')
    } else {
      setSection && setSection(id, SECTION_STATE.TYPING)
    }
  }

  promptICD10Lookup = async () => {
    const { id } = this.props
    const { sectionNameValue, content } = this.state
    // stop dictation before lookup
    await this.stopSectionDictation({ disableExitSound: true })
    promptICD10Lookup(id, sectionNameValue, false, content)
  }

  // saveSectionName either adds the new problem section to the composition, or updates an existing section name
  saveSectionName = async (sectionId, name) => {
    const {
      setAudioStreamContext,
      updateProblemSection
    } = this.props

    const compositionData = await getCompFromCache(loSto.get(LO_STO.CURRENT_COMP_ID))

    // if section name is duplicate, put section back into section name, and dont sent update
    const existingSectionNames = compositionData?.sectionsS2
      ?.filter(s => s.id !== sectionId)
      ?.map(s => s.name)

    if (existingSectionNames.includes(name)) {
      const idTag = `section-name-input-${sectionId}`
      const inputElement = document.getElementById(idTag)
      inputElement && inputElement.focus()
      setToast(TOAST.TYPE.WARNING, 'This note already has a section with that name', TOAST.DURATION.MEDIUM)
      setTimeout(async () => {
        if (document.activeElement !== inputElement) {
          const oldSectionName = compositionData?.sectionsS2?.find(s => s.id === sectionId)?.name
          if (oldSectionName === '') {
            deleteProblemSection(sectionId)
          }
          this.setState({ sectionNameValue: oldSectionName })
        }
      }, 400)
      return
    }

    hs.add(HALF_STORY.CLICK, `Updating Problem Section: ${sectionId}`)

    // once section is saved, icd10 search & autofill if possible
    // only if section doesnt have a icd10 attached
    const newSection = await updateProblemSection(sectionId, name)
    const hasIcdCode = !newSection?.diagnosisEntry?.diagnosis?.icdCode
    hasIcdCode && await autoPopulateICD10AfterAdd(newSection)

    setAudioStreamContext({
      problemBasedSections: await getPbSectionCtx(compositionData.id)
    }).then(() => {
      // restart audio to make sure noteId makes it to streaming-gateway
      sendSpeechConfig('New subsection has been added')
    })
  }

  render () {
    const {
      id,
      name,
      currentSectionId,
      currentSectionState,
      setSection,
      dictation,
      isProblemSection,
      isNewSection,
      diagnosisCount,
      handleDeleteSection,
      compositionId,
      section,
      stopDictation,
      keyboardMode,
      deleteEmptyProblemSection
    } = this.props

    const { sectionNameValue } = this.state
    const icdCode = get(section, 'diagnosisEntry.diagnosis.icdCode')
    if (!id) return null
    if (isProblemSection) {
      return (
        <ProblemSection
          id={id}
          name={sectionNameValue}
          icdCode={icdCode}
          isNewSection={isNewSection}
          currentSectionId={currentSectionId}
          currentSectionState={currentSectionState}
          setSection={setSection}
          dictation={dictation}
          keyboardMode={keyboardMode}
          diagnosisCount={diagnosisCount}
          handleDeleteSection={handleDeleteSection}
          deleteEmptyProblemSection={deleteEmptyProblemSection}
          saveSectionName={this.saveSectionName}
          compositionId={compositionId}
          handleSectionNameChange={this.onSectionNameChange}
          editor={!isNewSection && this.renderEditor()}
          handleSectionClick={this.onSectionClick}
          promptICD10Lookup={this.promptICD10Lookup}
          promptCodePreview={this.promptCodePreview}
          handleSectionDictation={this.onDictationClick}
          scrollToSection={scrollToSection}
          stopDictation={stopDictation}
          handleNameInputClick={this.onNameInputClick}
          handleNameKeyDown={this.onNameKeyDown}
        />
      )
    } else {
      return (
        <NoteSection
          id={id}
          name={name}
          currentSectionId={currentSectionId}
          currentSectionState={currentSectionState}
          setSection={setSection}
          editor={this.renderEditor()}
          dictation={dictation}
          keyboardMode={keyboardMode}
          handleSectionClick={this.onSectionClick}
          handleSectionDictation={this.onDictationClick}
          stopDictation={stopDictation}
        />
      )
    }
  }
}

export default compose(
  graphql(GET_KEYBOARD_MODE, {
    props: ({ data: { keyboardMode } }) => {
      return { keyboardMode }
    }
  }),
  graphql(SET_AGENT_STATE, {
    props: ({ mutate }) => ({
      setAgentState: agentState => mutate({
        variables: { agentState }
      })
    })
  }),
  graphql(GET_DICTATION, {
    props: ({ data: { dictation } }) => ({
      dictation
    })
  }),
  withApollo
)(Section)
